/**
 * Locale-aware formatting helpers (architecture §6.1). Pure `Intl` wrappers shared by the
 * web app (bound to the active UI language) and the server (bound to the recipient's
 * `user.language`) so a date or price reads the same wherever it is rendered.
 *
 * Our two locales map to full BCP-47 tags with the right regional conventions for a
 * Spain-based B2B operator: Spanish → `es-ES`, English → British `en-GB` (day/month order,
 * 24h-friendly, € as the working currency). The default trade currency is the euro.
 */
import { defaultLocale, type Locale } from "./index";

const BCP47: Record<Locale, string> = {
  es: "es-ES",
  en: "en-GB",
};

/** The default currency for the platform (TC operates in euros). */
export const DEFAULT_CURRENCY = "EUR";

/** Resolve a `Locale` to the BCP-47 tag `Intl` expects, falling back to the default locale. */
export function intlLocale(locale: Locale): string {
  return BCP47[locale] ?? BCP47[defaultLocale];
}

/** Accepts the values callers realistically hold: a `Date`, an epoch ms number, or an ISO string. */
export type DateInput = Date | number | string;

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/** Default option sets, exported so callers can extend them (e.g. add a `timeZone`) without
 * duplicating the medium-date / medium-date+time shapes. */
export const DATE_OPTIONS_DEFAULT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};
export const DATE_TIME_OPTIONS_DEFAULT: Intl.DateTimeFormatOptions = {
  ...DATE_OPTIONS_DEFAULT,
  hour: "2-digit",
  minute: "2-digit",
};

/** Format a date (default: medium date, no time) in the given locale, e.g. `14 Jun 2026`. */
export function formatDate(
  locale: Locale,
  value: DateInput,
  options: Intl.DateTimeFormatOptions = DATE_OPTIONS_DEFAULT,
): string {
  return new Intl.DateTimeFormat(intlLocale(locale), options).format(toDate(value));
}

/** Format a date with time (default: medium date + short time) in the given locale. */
export function formatDateTime(
  locale: Locale,
  value: DateInput,
  options: Intl.DateTimeFormatOptions = DATE_TIME_OPTIONS_DEFAULT,
): string {
  return new Intl.DateTimeFormat(intlLocale(locale), options).format(toDate(value));
}

/** Format a number in the given locale (thousands/decimal separators per region). */
export function formatNumber(
  locale: Locale,
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(intlLocale(locale), options).format(value);
}

/**
 * Format a date relative to a reference instant, e.g. `in 3 days` / `dentro de 3 días`,
 * `yesterday` / `ayer`, `today` / `hoy`. Built on `Intl.RelativeTimeFormat` (`numeric: "auto"`
 * yields the idiomatic "today/tomorrow/yesterday" where the locale has them). The unit is chosen
 * by magnitude: days within a fortnight, then weeks, then months — enough for trip countdowns
 * without pretending to day-level precision months out. `base` defaults to now.
 */
export function formatRelative(locale: Locale, value: DateInput, base: DateInput = new Date()): string {
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: "auto" });
  const days = Math.round((startOfDay(toDate(value)) - startOfDay(toDate(base))) / 86_400_000);
  if (Math.abs(days) < 14) return rtf.format(days, "day");
  if (Math.abs(days) < 60) return rtf.format(Math.round(days / 7), "week");
  return rtf.format(Math.round(days / 30), "month");
}

/** Midnight (local) of the given date, so relative formatting counts whole calendar days. */
function startOfDay(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Localized country name for an ISO 3166-1 alpha-2 code, e.g. `ES` → `España` / `Spain`. Falls
 * back to the input unchanged when it isn't a recognized region code, so legacy free-text values
 * entered before the country picker existed still render as-is.
 */
export function formatCountry(locale: Locale, code: string): string {
  try {
    return new Intl.DisplayNames([intlLocale(locale)], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Format a currency amount (default: EUR) in the given locale, e.g. `1.250,00 €` / `€1,250.00`. */
export function formatCurrency(
  locale: Locale,
  value: number,
  currency: string = DEFAULT_CURRENCY,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: "currency",
    currency,
    ...options,
  }).format(value);
}
